import { useState, useEffect, useRef } from "react";
import { Send, Bot, User, Loader2, Plus, Trash2, MessageSquare, ChevronRight, Lightbulb, ShoppingCart, Users, TrendingUp, DollarSign } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import axios from "axios";
import ChatMessageRenderer from "@/components/ChatMessageRenderer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PurchasingChat = () => {
  const [sessions, setSessions] = useState([]);
  const [currentSessionId, setCurrentSessionId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const endRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => { fetchSessions(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const fetchSessions = async () => {
    try { setSessions((await axios.get(`${API}/purchasing-agent/sessions`)).data); } catch (e) { console.error(e); }
  };

  const loadSession = async (id) => {
    setLoading(true);
    try {
      setMessages((await axios.get(`${API}/purchasing-agent/sessions/${id}/messages`)).data);
      setCurrentSessionId(id);
    } catch (e) { toast.error("Failed to load session"); }
    finally { setLoading(false); }
  };

  const startNew = () => {
    setCurrentSessionId(null); setMessages([]);
    setSuggestions(["Summarize procurement health", "Analyze vendor performance", "Show spend breakdown", "Identify savings opportunities"]);
    inputRef.current?.focus();
  };

  const send = async (text = input) => {
    if (!text.trim() || sending) return;
    const userMsg = { role: "user", content: text, timestamp: new Date().toISOString() };
    setMessages((p) => [...p, userMsg]); setInput(""); setSending(true); setSuggestions([]);
    try {
      const res = await axios.post(`${API}/purchasing-agent/chat`, { message: text, session_id: currentSessionId });
      setMessages((p) => [...p, { role: "assistant", content: res.data.response, timestamp: new Date().toISOString() }]);
      setCurrentSessionId(res.data.session_id);
      setSuggestions(res.data.suggestions || []);
      fetchSessions();
    } catch (e) {
      toast.error("Failed to get response"); setMessages((p) => p.slice(0, -1)); setInput(text);
    } finally { setSending(false); }
  };

  const deleteSession = async (id, e) => {
    e.stopPropagation();
    try {
      await axios.delete(`${API}/purchasing-agent/sessions/${id}`);
      setSessions((p) => p.filter((s) => s.id !== id));
      if (currentSessionId === id) startNew();
      toast.success("Deleted");
    } catch (e) { toast.error("Failed to delete"); }
  };

  const fmtTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <div className="flex gap-4 h-[calc(100vh-320px)]" data-testid="pu-chat">
      <div className="w-64 flex-shrink-0">
        <Card className="h-full card-industrial">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2"><MessageSquare className="w-4 h-4" /> Sessions</CardTitle>
              <Button size="sm" variant="outline" onClick={startNew} className="h-7 text-xs" data-testid="pu-new-session"><Plus className="w-3 h-3 mr-1" /> New</Button>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <ScrollArea className="h-[calc(100vh-420px)]">
              <div className="px-3 pb-3 space-y-1.5">
                {sessions.length === 0 ? (
                  <p className="text-center text-xs text-muted-foreground py-6">No sessions yet</p>
                ) : sessions.map((s) => (
                  <div key={s.id} className={`group flex items-center gap-2 p-2 rounded cursor-pointer text-xs transition-colors ${currentSessionId === s.id ? "bg-primary/10 border border-primary/20" : "hover:bg-secondary"}`} onClick={() => loadSession(s.id)}>
                    <div className="flex-1 min-w-0"><p className="font-medium truncate">{s.title}</p><p className="text-muted-foreground">{s.message_count} msgs</p></div>
                    <Button size="icon" variant="ghost" className="h-5 w-5 opacity-0 group-hover:opacity-100" onClick={(e) => deleteSession(s.id, e)}><Trash2 className="w-3 h-3" /></Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <div className="flex-1 flex flex-col">
        <Card className="flex-1 card-industrial flex flex-col">
          <CardHeader className="pb-2 border-b">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-full bg-gradient-to-br from-violet-600 to-purple-500 flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-white" />
              </div>
              <div>
                <CardTitle className="text-base">Purchasing Analytics AI</CardTitle>
                <p className="text-xs text-muted-foreground">Ask about procurement, vendors, spend analysis</p>
              </div>
              <Badge variant="secondary" className="ml-auto"><span className="w-2 h-2 rounded-full bg-violet-500 mr-1.5 animate-pulse" />Online</Badge>
            </div>
          </CardHeader>

          <CardContent className="flex-1 p-0 flex flex-col">
            <ScrollArea className="flex-1 p-4">
              {loading ? (
                <div className="space-y-4">{[...Array(3)].map((_, i) => (<div key={i} className="flex gap-3"><Skeleton className="w-8 h-8 rounded-full" /><Skeleton className="h-16 flex-1 rounded-xl" /></div>))}</div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center py-8">
                  <div className="w-14 h-14 rounded-full bg-violet-50 flex items-center justify-center mb-3"><ShoppingCart className="w-7 h-7 text-violet-600" /></div>
                  <h3 className="text-base font-medium mb-1">Purchasing Analyst</h3>
                  <p className="text-xs text-muted-foreground max-w-sm mb-4">I can analyze procurement data, vendor performance, spend patterns, and identify savings.</p>
                  <div className="grid grid-cols-2 gap-2 max-w-sm">
                    {[
                      { icon: DollarSign, label: "Spend Analysis", q: "Analyze our total spend breakdown by category" },
                      { icon: Users, label: "Vendor Review", q: "Review vendor performance and identify underperformers" },
                      { icon: ShoppingCart, label: "PO Status", q: "Show current purchase order status summary" },
                      { icon: TrendingUp, label: "Savings", q: "Identify cost savings opportunities in procurement" },
                    ].map((item) => (
                      <button key={item.label} className="p-2.5 rounded-lg bg-secondary hover:bg-secondary/80 transition-colors text-left" onClick={() => send(item.q)} data-testid={`pu-quick-${item.label.toLowerCase().replace(/\s/g, "-")}`}>
                        <item.icon className="w-4 h-4 text-violet-600 mb-1" />
                        <p className="text-xs font-medium">{item.label}</p>
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {messages.map((msg, idx) => (
                    <div key={idx} className={`flex gap-2.5 ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
                      {msg.role !== "user" && <div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0"><Bot className="w-3.5 h-3.5 text-violet-700" /></div>}
                      <div className="max-w-[80%]">
                        <div className={`rounded-xl px-3 py-2 ${msg.role === "user" ? "bg-primary text-primary-foreground rounded-tr-sm" : "bg-secondary rounded-tl-sm"}`}>
                          <ChatMessageRenderer content={msg.content} />
                        </div>
                        <p className={`text-[10px] text-muted-foreground mt-0.5 ${msg.role === "user" ? "text-right" : ""}`}>{fmtTime(msg.timestamp)}</p>
                      </div>
                      {msg.role === "user" && <div className="w-7 h-7 rounded-full bg-primary flex items-center justify-center flex-shrink-0"><User className="w-3.5 h-3.5 text-primary-foreground" /></div>}
                    </div>
                  ))}
                  {sending && (
                    <div className="flex gap-2.5"><div className="w-7 h-7 rounded-full bg-violet-100 flex items-center justify-center"><Bot className="w-3.5 h-3.5 text-violet-700" /></div><div className="bg-secondary rounded-xl rounded-tl-sm px-3 py-2"><div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-3.5 h-3.5 animate-spin" /><span className="text-xs">Analyzing...</span></div></div></div>
                  )}
                  <div ref={endRef} />
                </div>
              )}
            </ScrollArea>

            {suggestions.length > 0 && (
              <div className="px-3 py-2 border-t bg-secondary/30">
                <div className="flex items-center gap-1.5 mb-1"><Lightbulb className="w-3 h-3 text-amber-500" /><span className="text-[10px] text-muted-foreground">Suggestions</span></div>
                <div className="flex flex-wrap gap-1.5">
                  {suggestions.map((s, i) => (
                    <button key={i} className="px-2.5 py-1 text-[11px] rounded-full bg-background border hover:bg-secondary transition-colors" onClick={() => send(s)}>{s}<ChevronRight className="w-3 h-3 inline ml-1" /></button>
                  ))}
                </div>
              </div>
            )}

            <div className="p-3 border-t">
              <div className="flex gap-2">
                <Input ref={inputRef} placeholder="Ask about procurement, vendors, spend..." value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} disabled={sending} className="flex-1 text-sm" data-testid="pu-chat-input" />
                <Button onClick={() => send()} disabled={!input.trim() || sending} size="sm" data-testid="pu-send-btn">
                  {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </Button>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1 text-center">Powered by GPT-5.2</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default PurchasingChat;
